import { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect, useRef } from 'react';
import {
  CreateVisitFormV2Data,
  WhenStepFormData,
  HostStepFormData,
  VisitStepFormData,
  MonthlyMode,
  RepeatType,
  HostingType,
  VisitorMode,
} from '../types/form-data.types';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useVisitState } from 'store/visits/hooks';
import { Visit } from 'store/visits/types';
import { fetchVisitSeries } from 'store/visits/actions';
import { getVisitSeriesState } from 'store/visits/selectors';
import { getSeriesUuidFromVisit, parseSeriesArrivalDates } from 'store/visits/multi-day.utils';
import { useMultiDayVisitsEnabled } from 'hooks/use-multi-day-visits.hook';
import { OwnerParams } from 'shared/types';
import { VisitorFrequencyEnum } from 'components/visit-form/types';
import { useTimeZone } from 'shared/hooks';
import { toTimeValue } from 'utils/time-value';
import {
  snapshotPrimaryVisitorContactFromVisit,
  type PrimaryVisitorContactSnapshot,
} from '../primary-visitor-contact.util';
import { datesToDateOnlyStrings } from '../utils/visit-dates.utils';

interface CreateVisitFormV2ContextValue {
  formData: CreateVisitFormV2Data;
  updateVisitStepData: (data: VisitStepFormData) => void;
  getWhenStepData: () => WhenStepFormData | undefined;
  getHostStepData: () => HostStepFormData | undefined;
  getVisitStepData: () => VisitStepFormData | undefined;
  // Edit mode
  isEditMode: boolean;
  isLoadingVisit: boolean;
  // Version that increments when edit data is loaded (used to trigger form resets)
  editDataVersion: number;
  /** Normalized primary visitor email/phone when the visit was loaded (edit mode only). */
  baselinePrimaryVisitorContact: PrimaryVisitorContactSnapshot | null;
  seriesUuid: string | null;
  isExistingSeries: boolean;
  canModifySeriesDates: boolean;
  originalSeriesDates: string[];
}

const CreateVisitFormV2Context = createContext<CreateVisitFormV2ContextValue | undefined>(undefined);

/**
 * Infer the UI RepeatType from the API frequency + recurrence_days.
 *
 * Primary mapping comes from frequency:
 *   - daily      -> DAILY
 *   - work-days  -> WEEKDAY
 *   - weekly     -> derive from recurrence_days
 *
 * For weekly values we derive intent from day flags:
 *   - all 7 days true  -> DAILY (backward compatibility)
 *   - Mon–Fri only     -> WEEKDAY (backward compatibility)
 *   - exactly 1 day    -> WEEKLY
 *   - any other mix    -> CUSTOM
 */
export const inferRepeatType = (
  frequency?: VisitorFrequencyEnum,
  recurrenceDays?: Visit['recurrence_days'],
  monthlyMode?: MonthlyMode | null,
): RepeatType => {
  if (!frequency || frequency === VisitorFrequencyEnum.ONCE) {
    return RepeatType.NONE;
  }

  if (frequency === VisitorFrequencyEnum.MONTHLY) {
    return monthlyMode === MonthlyMode.DAY_OF_WEEK ? RepeatType.MONTHLY_DAY_OF_WEEK : RepeatType.MONTHLY_DAY_OF_MONTH;
  }

  const canDeriveFromRecurrenceDays =
    [VisitorFrequencyEnum.WEEKLY, VisitorFrequencyEnum.WORK_DAYS, VisitorFrequencyEnum.DAILY].includes(frequency) &&
    recurrenceDays &&
    ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].some(
      (d) => recurrenceDays[d as keyof typeof recurrenceDays] === true,
    );

  if (canDeriveFromRecurrenceDays) {
    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
    const activeDays = allDays.filter((d) => recurrenceDays[d] === true);

    if (activeDays.length === 7) {
      return RepeatType.DAILY;
    }

    const isWeekdayOnly =
      ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].every(
        (d) => recurrenceDays[d as keyof typeof recurrenceDays] === true,
      ) &&
      !recurrenceDays.saturday &&
      !recurrenceDays.sunday;
    if (isWeekdayOnly) {
      return RepeatType.WEEKDAY;
    }

    if (activeDays.length === 1) {
      return RepeatType.WEEKLY;
    }

    return RepeatType.CUSTOM;
  }

  // Fallback when recurrence_days is missing.
  if (frequency === VisitorFrequencyEnum.DAILY) {
    return RepeatType.DAILY;
  }
  if (frequency === VisitorFrequencyEnum.WORK_DAYS) {
    return RepeatType.WEEKDAY;
  }
  if (frequency === VisitorFrequencyEnum.WEEKLY) {
    return RepeatType.WEEKLY;
  }

  return RepeatType.NONE;
};

/**
 * Calculate duration in hours between two dates
 */
const calculateDurationFromDates = (startDate: Date, endDate: Date | null): number | undefined => {
  if (!endDate) {
    return undefined;
  }
  const diffMs = endDate.getTime() - startDate.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  return diffHours > 0 ? diffHours : undefined;
};

/**
 * Maps a Visit from the API to the form data structure
 */
const mapVisitToFormData = (visit: Visit, timeZone: string, seriesDates?: Date[]): CreateVisitFormV2Data => {
  const arrivalDate = new Date(visit.arrival_date);
  const departureDate = visit.departure_date ? new Date(visit.departure_date) : null;
  const visitDatesFromSeries = visit.series_dates?.map((value) => new Date(`${value}T00:00:00`));
  let visitDates = [arrivalDate];
  if (seriesDates && seriesDates.length > 0) {
    visitDates = seriesDates;
  } else if (visitDatesFromSeries && visitDatesFromSeries.length > 0) {
    visitDates = visitDatesFromSeries;
  }
  const isMultiDay = visitDates.length > 1;
  const primaryDate = visitDates[0] ?? arrivalDate;

  // Calculate duration from the time difference
  const duration = calculateDurationFromDates(arrivalDate, departureDate);

  // Determine repeat type and validate repeatEndDate
  const repeatType = inferRepeatType(visit.frequency, visit.recurrence_days, visit.monthly_mode);
  let repeatEndDate: Date | null = null;

  // Only set repeatEndDate if there's a repeat type and the end_date is valid (after arrival)
  if (repeatType !== RepeatType.NONE && visit.end_date) {
    const endDate = new Date(visit.end_date);
    // Only use the end_date if it's after the arrival date
    if (endDate > arrivalDate) {
      repeatEndDate = endDate;
    }
  }

  const whenData: WhenStepFormData = {
    visitStartDate: primaryDate,
    visitEndDate: primaryDate,
    startTime: toTimeValue(visit.arrival_date, timeZone),
    endTime: visit.departure_date ? toTimeValue(visit.departure_date, timeZone) : null,
    duration,
    repeatType: isMultiDay ? RepeatType.NONE : repeatType,
    repeatEndDate: isMultiDay ? null : repeatEndDate,
    recurrenceDays: !isMultiDay && repeatType === RepeatType.CUSTOM ? visit.recurrence_days ?? undefined : undefined,
    isMultiDay,
    visitDates,
  };

  const hostData: HostStepFormData = {
    hostingType: visit.host?.email ? HostingType.OTHER : HostingType.SELF,
    floor: visit.floor || '',
    suite: visit.suite || null,
    hostEmail: visit.host?.email || null,
    firstName: visit.host?.first_name || '',
    lastName: visit.host?.last_name || '',
    // Edit mode fields for Host preferences
    receiveInvitationCopy: visit.send_invitation_to_host ?? true,
    receiveCheckinNotifications: visit.notify_on_check_in ?? true,
    additionalOrganizers:
      visit.email_recipients?.filter((email) => email.toLowerCase() !== visit.host?.email?.toLowerCase()).join(', ') ??
      null,
  };

  const visitData: VisitStepFormData = {
    visitorMode: visit.visitors && visit.visitors.length > 1 ? VisitorMode.MULTIPLE : VisitorMode.SINGLE,
    visitSummary: visit.reason_for_visit || visit.description || '',
    visitTypeId: visit.visit_type_id != null ? String(visit.visit_type_id) : '',
    checkInInstructions: visit.special_instruction_for_visitor ?? '',
    noteForReception: visit.special_instruction_for_receptionist ?? '',
    visitEntryOption: visit.pass_entry_option ?? '',
    visitStatus: visit.visit_status,
    email_opt_in: visit.email_opt_in,
    sms_opt_in: visit.sms_opt_in,
    visitors:
      visit.visitors?.map((v) => ({
        id: v.id ?? undefined,
        firstName: v.first_name,
        lastName: v.last_name,
        email: v.email || '',
        phone: v.phone || v.visitor_phone || '',
        companyName: v.company || '',
        photoUuid: v.photo_uuid || '',
      })) || [],
  };

  return {
    when: whenData,
    host: hostData,
    visit: visitData,
  };
};

interface CreateVisitFormV2ProviderProps {
  children: ReactNode;
}

export const CreateVisitFormV2Provider = ({ children }: CreateVisitFormV2ProviderProps): JSX.Element => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<CreateVisitFormV2Data>({});
  const [editDataVersion, setEditDataVersion] = useState(0);
  const [baselinePrimaryVisitorContact, setBaselinePrimaryVisitorContact] =
    useState<PrimaryVisitorContactSnapshot | null>(null);
  const { visitId, ownerId } = useParams<OwnerParams & { visitId?: string }>();
  const isEditMode = Boolean(visitId);
  const visitState = useVisitState(visitId || '', !isEditMode);
  const seriesState = useSelector(getVisitSeriesState);
  const multiDayEnabled = useMultiDayVisitsEnabled();
  const timeZone = useTimeZone();
  const seriesUuid = visitState.data ? getSeriesUuidFromVisit(visitState.data) : null;
  const isExistingSeries = Boolean(seriesUuid);
  const canModifySeriesDates = multiDayEnabled || !isExistingSeries;
  const requestedSeriesIdRef = useRef<string | null>(null);
  const hasMatchingSeries = Boolean(seriesUuid && seriesState.data?.seriesUuid === seriesUuid);
  const isWaitingForSeries = Boolean(seriesUuid) && !seriesState.error && !hasMatchingSeries;

  useEffect(() => {
    if (!isEditMode || !visitState.data || visitState.loading || !seriesUuid || !ownerId) {
      return;
    }
    if (seriesState.loading || hasMatchingSeries) {
      return;
    }
    if (seriesState.error && requestedSeriesIdRef.current === seriesUuid) {
      return;
    }
    requestedSeriesIdRef.current = seriesUuid;
    dispatch(fetchVisitSeries.request({ buildingId: ownerId, seriesId: seriesUuid }));
  }, [
    dispatch,
    hasMatchingSeries,
    isEditMode,
    ownerId,
    seriesState.error,
    seriesState.loading,
    seriesUuid,
    visitState.data,
    visitState.loading,
  ]);

  useEffect(() => {
    if (!isEditMode) {
      setBaselinePrimaryVisitorContact(null);
      return;
    }

    if (!visitState.data || visitState.loading || !timeZone) {
      return;
    }

    if (isWaitingForSeries || seriesState.loading) {
      return;
    }

    const seriesDates =
      hasMatchingSeries && seriesState.data ? parseSeriesArrivalDates(seriesState.data.dates) : undefined;
    const mappedData = mapVisitToFormData(visitState.data, timeZone, seriesDates);
    setFormData(mappedData);
    setBaselinePrimaryVisitorContact(snapshotPrimaryVisitorContactFromVisit(visitState.data));
    setEditDataVersion((v) => v + 1);
  }, [
    hasMatchingSeries,
    isEditMode,
    isWaitingForSeries,
    seriesState.data,
    seriesState.loading,
    timeZone,
    visitState.data,
    visitState.loading,
  ]);

  const updateVisitStepData = useCallback((data: VisitStepFormData): void => {
    setFormData((prev) => ({
      ...prev,
      visit: data,
    }));
  }, []);

  const getWhenStepData = useCallback((): WhenStepFormData | undefined => formData.when, [formData.when]);

  const getHostStepData = useCallback((): HostStepFormData | undefined => formData.host, [formData.host]);

  const getVisitStepData = useCallback((): VisitStepFormData | undefined => formData.visit, [formData.visit]);

  const originalSeriesDates = useMemo(
    () => (formData.when?.visitDates ? datesToDateOnlyStrings(formData.when.visitDates) : []),
    [formData.when?.visitDates],
  );

  const isLoadingVisit = isEditMode && (visitState.loading || seriesState.loading || isWaitingForSeries);

  const value: CreateVisitFormV2ContextValue = useMemo(
    () => ({
      formData,
      updateVisitStepData,
      getWhenStepData,
      getHostStepData,
      getVisitStepData,
      isEditMode,
      isLoadingVisit,
      editDataVersion,
      baselinePrimaryVisitorContact,
      seriesUuid,
      isExistingSeries,
      canModifySeriesDates,
      originalSeriesDates,
    }),
    [
      formData,
      updateVisitStepData,
      getWhenStepData,
      getHostStepData,
      getVisitStepData,
      isEditMode,
      isLoadingVisit,
      editDataVersion,
      baselinePrimaryVisitorContact,
      seriesUuid,
      isExistingSeries,
      canModifySeriesDates,
      originalSeriesDates,
    ],
  );

  return <CreateVisitFormV2Context.Provider value={value}>{children}</CreateVisitFormV2Context.Provider>;
};

export const useCreateVisitFormV2Context = (): CreateVisitFormV2ContextValue => {
  const context = useContext(CreateVisitFormV2Context);
  if (context === undefined) {
    throw new Error('useCreateVisitFormV2Context must be used within a CreateVisitFormV2Provider');
  }
  return context;
};
